<template>
  <lb-dialog
    :value="value"
    @input="$emit('input', $event)"
    :heading="heading || `${$nova.labelsName('my_reporting_units_page', 'ru_entity_label', 'Reporting Unit')} Detail`"
    width="60%"
    :loading="loading"
    data-testid="reporting-unit-dialog"
  >
    <template v-slot:body>
      <div v-if="loading" class="d-flex flex-column gap-4" data-testid="reporting-unit-dialog__skeleton">
        <v-skeleton-loader type="heading" width="40%" class="mb-1"></v-skeleton-loader>
        <div class="d-flex gap-4">
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
        </div>
        <div class="d-flex flex-column gap-3">
          <v-card v-for="i in 6" :key="i" class="d-flex flex-column pa-4 usercard gap-3">
            <v-skeleton-loader type="text" width="150" class="mb-4"></v-skeleton-loader>
            <div class="d-flex flex-wrap gap-3">
               <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
               <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
            </div>
          </v-card>
        </div>
      </div>
      <div v-else class="d-flex flex-column gap-4" data-testid="reporting-unit-dialog__content">
        <template v-if="showReportingUnitDetails">
          <h4 class="font-size-md mb-1 noTransform" data-testid="reporting-unit-dialog__title">{{ detail.code }} -{{ detail.name }}</h4>
          <div class="d-flex gap-4">
            <v-chip class="chip-choose" data-testid="reporting-unit-dialog__network-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'division_label', 'Network') }} - {{ detail.network_detail?.name }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip class="chip-choose" data-testid="reporting-unit-dialog__region-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'region_label', 'Region') }} - {{ detail.region_detail?.name }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip class="chip-choose" data-testid="reporting-unit-dialog__country-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'country_label', 'Country') }} - {{ detail.country_detail?.name }}</span>
            </v-chip>
          </div>
        </template>
        <div class="d-flex flex-column gap-3">
          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__local-preparer-card">
            <h5 class="font-size-md">{{ $nova.labelsName('my_reporting_units_page', 'commentary_local_preparer_label', 'Local Preparer') }}</h5>
            <div class="d-flex flex-wrap gap-3" data-testid="reporting-unit-dialog__local-preparer-list">
              <template v-if="(detail.submitter_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.submitter_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__local-preparer-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__local-preparer-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>

          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__local-approver-card">
            <h5 class="font-size-md">{{ $nova.labelsName('my_reporting_units_page', 'commentary_local_approver_label', 'Local Approver') }}</h5>
            <div class="d-flex flex-wrap gap-3" data-testid="reporting-unit-dialog__local-approver-list">
              <template v-if="(detail.approver_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.approver_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__local-approver-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__local-approver-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>

          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__regional-approver-card">
            <h5 class="font-size-md">{{ $nova.labelsName('my_reporting_units_page', 'commentary_regional_approver_label', 'Regional Approver') }}</h5>
            <div class="d-flex flex-wrap gap-3" data-testid="reporting-unit-dialog__regional-approver-list">
              <template v-if="(detail.regional_approver_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.regional_approver_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__regional-approver-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__regional-approver-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>

          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__manager-card">
            <h5 class="font-size-md">Manager</h5>
            <div class="d-flex flex-wrap gap-2" data-testid="reporting-unit-dialog__manager-list">
              <template v-if="(detail.manager_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.manager_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__manager-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__manager-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>

          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__admin-preparer-card">
            <h5 class="font-size-md">Admin Preparer</h5>
            <div class="d-flex flex-wrap gap-2" data-testid="reporting-unit-dialog__admin-preparer-list">
              <template v-if="(detail.admin_preparer_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.admin_preparer_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__admin-preparer-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__admin-preparer-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>

          <v-card class="d-flex flex-column pa-4 usercard gap-3" data-testid="reporting-unit-dialog__admin-approver-card">
            <h5 class="font-size-md">Admin Approver</h5>
            <div class="d-flex flex-wrap gap-2" data-testid="reporting-unit-dialog__admin-approver-list">
              <template v-if="(detail.admin_approver_detail || []).length > 0">
                <v-chip
                  v-for="(user, index) in detail.admin_approver_detail"
                  :key="index"
                  :color="(userId === user._id) ? 'primary' : ''"
                  :class="['email-chip', { 'email-chip-bg': userId !== user._id }]"
                  text
                  data-testid="reporting-unit-dialog__admin-approver-chip"
                >
                  <v-avatar left class="mr-2">
                    <v-icon>mdi-account-outline</v-icon>
                  </v-avatar>
                  <div class="name-email">
                    <span v-if="user.name && user.name.trim()">{{ user.name }}</span>
                    <span v-if="user.name && user.name.trim() && user.email"> - </span>
                    <span class="textLowercase">{{ user.email }}</span>
                  </div>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text" data-testid="reporting-unit-dialog__admin-approver-empty">No Users assigned</div>
              </template>
            </div>
          </v-card>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
export default {
  name: 'ReportingUnitDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: () => ({}),
    },
    userId: {
      type: [String, Number],
      default: '',
    },
    heading: {
      type: String,
      default: '',
    },
    showReportingUnitDetails: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<style scoped>
</style>
